import { Button } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Select, Switch, message } from 'antd';

import { addTeacher } from 'requests/teacher';
import { getListClassDropdown } from 'requests/class';
import { validateEmail } from 'requests/auth';
import _ from 'lodash';

const { Option } = Select;

const AddTeacher = () => {
    const [loading, setLoading] = useState(false);
    const [classes, setClasses] = useState([]);
    const [classesTake, setClassesTake] = useState(20);
    const [classesTotal, setClassesTotal] = useState(21);
    const [classesSearch, setClassesSearch] = useState('');

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    useEffect(() => {
        return () => {
            sessionStorage.setItem('is_form_changed', 0);
        };
    }, []);

    const handleAddTeacher = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            for (const key in values) {
                if (typeof values[key] === 'string') values[key] = values[key].trim();
            }
            form.setFieldsValue(values);

            try {
                const email = form.getFieldValue('email');
                await validateEmail({ email });
                message.error(t('email_is_already_taken'));
                return;
            } catch (err) {
                if (err.response?.data.message !== 'email_not_found') throw err;
            }

            const data = values;
            await addTeacher(data);
            message.success(t('success'));
            navigate('/teachers');
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getClassData();
    }, [classesTake, classesSearch]);

    const getClassData = async () => {
        try {
            setLoading(true);

            const data = await getListClassDropdown({ take: classesTake, search: classesSearch, hasTeacher: 0 });
            const classOptions = data.records.map((records) => {
                return { label: records.className, value: records.id };
            });

            setClasses(classOptions);
            setClassesTotal(data.total);
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = _.debounce(setClassesSearch, 400);

    const handleScroll = (e) => {
        const target = e.target;
        if (
            !loading &&
            classesTake < classesTotal &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 10
        ) {
            setClassesTake(classesTake + 10);
        }
    };

    return (
        <div className="add-edit-page">
            <div className="add-edit-form">
                <h1 className="mt-32 mb-40">{t('add_teacher')}</h1>
                <div className="add-input">
                    <Form
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        layout="horizontal"
                        form={form}
                        initialValues={{ role: 2, status: 1 }}
                        onValuesChange={() => sessionStorage.setItem('is_form_changed', 1)}
                    >
                        <Form.Item
                            name="name"
                            label={t('teacher_name')}
                            rules={[{ required: true, pattern: /\S+/, message: t('required_field') }]}
                        >
                            <Input placeholder={t('input_name_add')} />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            label={t('password')}
                            extra={t('extra_password')}
                            rules={[
                                { required: true, message: t('required_field') },
                                {
                                    pattern: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/,
                                    message: t('invalid_format'),
                                },
                            ]}
                        >
                            <Input placeholder={t('input_password_add')} />
                        </Form.Item>

                        <Form.Item name="classes" label={t('class')}>
                            <Select
                                showSearch
                                filterOption={false}
                                onSearch={handleSearch}
                                mode="multiple"
                                style={{ width: '100%' }}
                                placeholder={t('input_class_add')}
                                optionLabelProp="label"
                                options={classes}
                                onPopupScroll={handleScroll}
                                onDropdownVisibleChange={() => setClassesSearch('')}
                            ></Select>
                        </Form.Item>

                        <Form.Item name="role" label={t('decentralization')}>
                            <Select placeholder={t('select_decentralization')} defaultValue={t('teacher')}>
                                <Option value={1}>{t('admin')}</Option>
                                <Option value={2}>{t('teacher')}</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: t('required_field') },
                                { type: 'email', message: t('email_validation_message') },
                            ]}
                        >
                            <Input placeholder={t('input_email_add')} />
                        </Form.Item>

                        <Form.Item
                            name="mobile"
                            label={t('mobile')}
                            rules={[{ pattern: /^[\d\-+ ]+$/, message: t('phone_number_message') }]}
                        >
                            <Input placeholder={t('input_phone_add')} />
                        </Form.Item>

                        <Form.Item name="status" label={t('status')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Form>
                </div>
            </div>
            <div className="add-edit-btn">
                <Button onClick={() => navigate(-1)} className="ant-btn cancel-btn">
                    {t('cancel')}
                </Button>
                <Button className="ant-btn save-btn" onClick={handleAddTeacher} loading={loading}>
                    {t('save')}
                </Button>
            </div>
        </div>
    );
};

export default AddTeacher;
